/**
 * AboutPage
 * Full standalone /about page. Sections:
 * 1. About       — wooden background (Tech Section style) with gradient title and tagline.
 * 2. Our Vision  — dark starfield background (Show Section style) with mission + pillar cards.
 * 3. Meet Mox    — white background (App Section style) with Mox bio and team tagline.
 * 4. Connect     — wooden background (Dashboard About Section style) with socials and email.
 * 5. Footer      — same as dashboard.
 */
package org.moxultimate.topwithmox.about

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.moxultimate.topwithmox.components.footer.Footer
import org.moxultimate.topwithmox.dashboard.WallpaperCarouselStyle
import org.moxultimate.topwithmox.components.image.SocialIcon
import org.moxultimate.topwithmox.components.texts.DescriptionText
import org.moxultimate.topwithmox.components.texts.TitleText
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.statics.AboutStatics
import org.moxultimate.topwithmox.statics.AboutStatics.CONNECT_DESC
import org.moxultimate.topwithmox.statics.AboutStatics.CONNECT_TITLE
import org.moxultimate.topwithmox.statics.AboutStatics.CONTENTS_TITLE
import org.moxultimate.topwithmox.statics.AboutStatics.CONTENT_PILLARS
import org.moxultimate.topwithmox.statics.AboutStatics.EMAIL_ADDRESS
import org.moxultimate.topwithmox.statics.AboutStatics.EMAIL_LABEL
import org.moxultimate.topwithmox.statics.AboutStatics.EMAIL_LINK
import org.moxultimate.topwithmox.statics.AboutStatics.HERO_TAGLINE
import org.moxultimate.topwithmox.statics.AboutStatics.HERO_TITLE_1
import org.moxultimate.topwithmox.statics.AboutStatics.HERO_TITLE_2
import org.moxultimate.topwithmox.statics.AboutStatics.IMG_WOOD_BOTTOM
import org.moxultimate.topwithmox.statics.AboutStatics.IMG_WOOD_TOP
import org.moxultimate.topwithmox.statics.AboutStatics.MOX_DESC
import org.moxultimate.topwithmox.statics.AboutStatics.MOX_GALLERY_IMAGES
import org.moxultimate.topwithmox.statics.AboutStatics.MOX_TITLE
import org.moxultimate.topwithmox.statics.AboutStatics.SOCIAL_EMAIL_ICON
import org.moxultimate.topwithmox.statics.AboutStatics.SOCIAL_FACEBOOK_ICON
import org.moxultimate.topwithmox.statics.AboutStatics.SOCIAL_FACEBOOK_LINK
import org.moxultimate.topwithmox.statics.AboutStatics.SOCIAL_INSTAGRAM_ICON
import org.moxultimate.topwithmox.statics.AboutStatics.SOCIAL_INSTAGRAM_LINK
import org.moxultimate.topwithmox.statics.AboutStatics.SOCIAL_MAIL_ICON
import org.moxultimate.topwithmox.statics.AboutStatics.SOCIAL_TIKTOK_ICON
import org.moxultimate.topwithmox.statics.AboutStatics.SOCIAL_TIKTOK_LINK
import org.moxultimate.topwithmox.statics.AboutStatics.SOCIAL_YOUTUBE_ICON
import org.moxultimate.topwithmox.statics.AboutStatics.SOCIAL_YOUTUBE_LINK
import org.moxultimate.topwithmox.statics.AboutStatics.SUBTITLE
import org.moxultimate.topwithmox.statics.AboutStatics.TEAM_TAGLINE
import org.moxultimate.topwithmox.statics.AboutStatics.VISION_DESC
import org.moxultimate.topwithmox.statics.AboutStatics.VISION_TITLE
import org.moxultimate.topwithmox.theme.DarkGrayColor
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.theme.WhiteBG

private const val IMG_BG_STARFIELD = "/bg_wallpaper_section.webp"

/**
 * AboutPage
 * Entry composable for the /about route.
 * Switches between mobile and desktop layouts based on breakpoint.
 */
@OptIn(DelicateApi::class)
@Composable
fun AboutPage() {
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM

    Column(modifier = Modifier.fillMaxWidth().backgroundColor(WhiteBG)) {
        AboutHeroSection(isMobile)
        AboutVisionSection(isMobile)
        AboutMoxSection(isMobile)
        AboutConnectSection(isMobile)
        Footer()
    }
}


// ─────────────────────────────────────────────────────────────────────────────
// 1. Hero Section — Tech Section bg (wood)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AboutHeroSection
 * Wooden-background hero (Tech Section style) with gradient two-line title and tagline.
 *
 * @param isMobile True when rendering on a small screen.
 */
@OptIn(DelicateApi::class)
@Composable
private fun AboutHeroSection(isMobile: Boolean) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(if (isMobile) 480.px else clamp(min = 560.px, value = 55.vh, max = 900.px))
            .styleModifier {
                property("position", "relative")
                property("background-image", "url('$IMG_WOOD_TOP')")
                property("background-size", "cover")
                property("background-position", "center")
            },
        contentAlignment = Alignment.Center,
    ) {
        // Absolute-positioned background that always fills the fixed-height box
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("height", "100%")
                    property("z-index", "0")
                },
        ) {
            Image(
                src = IMG_WOOD_TOP,
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1")
                        property("object-fit", "cover")
                        property("display", "block")
                        property("min-height", "0")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
            Image(
                src = IMG_WOOD_BOTTOM,
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1")
                        property("object-fit", "cover")
                        property("display", "block")
                        property("min-height", "0")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
        }

        Column(
            modifier = (if (isMobile)
                Modifier.fillMaxWidth().padding(leftRight = 32.px, top = 80.px)
            else
                Modifier.width(clamp(min = 800.px, value = 70.vw, max = 1400.px)).padding(top = 80.px))
                .styleModifier { property("z-index", "1") },
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(12.px),
        ) {
            SpanText(
                text = SUBTITLE,
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(if (isMobile) 11.px else 13.px)
                    .fontWeight(FontWeight.Normal)
                    .letterSpacing(0.15.cssRem)
                    .color(Colors.White)
                    .styleModifier { property("opacity", "0.75") },
            )

            SpanText(
                text = HERO_TITLE_1,
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(if (isMobile) 42.px else clamp(min = 48.px, value = 5.5.vw, max = 80.px))
                    .fontWeight(FontWeight.Bold)
                    .moxTextGradient(),
            )

            SpanText(
                text = HERO_TITLE_2,
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(if (isMobile) 32.px else clamp(min = 36.px, value = 4.vw, max = 60.px))
                    .fontWeight(FontWeight.Bold)
                    .moxTextGradient(),
            )

            SpanText(
                text = HERO_TAGLINE,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 16.px)
                    .fontFamily(FontFamily)
                    .fontSize(if (isMobile) 14.px else clamp(min = 14.px, value = 1.1.vw, max = 18.px))
                    .fontWeight(FontWeight.Normal)
                    .color(Colors.White)
                    .textAlign(TextAlign.Center)
                    .styleModifier { property("opacity", "0.85") },
            )
        }
    }
}


// ─────────────────────────────────────────────────────────────────────────────
// 2. Vision Section — Show Section bg (dark starfield)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AboutVisionSection
 * Dark-starfield background (Show Section style): mission statement + content-pillar cards.
 *
 * @param isMobile True when rendering on a small screen.
 */
@Composable
private fun AboutVisionSection(isMobile: Boolean) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("background-image", "url('$IMG_BG_STARFIELD')")
                property("background-size", "cover")
                property("background-position", "center")
                property("background-repeat", "no-repeat")
            }
            .padding(topBottom = 80.px),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = if (isMobile)
                Modifier.fillMaxWidth().padding(leftRight = 24.px)
            else
                Modifier.width(clamp(min = 1100.px, value = 80.vw, max = 2000.px)),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(20.px),
        ) {
            TitleText(title = VISION_TITLE)

            SpanText(
                text = VISION_DESC,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 8.px)
                    .fontFamily(FontFamily)
                    .fontSize(if (isMobile) 14.px else clamp(min = 14.px, value = 1.1.vw, max = 18.px))
                    .fontWeight(FontWeight.Normal)
                    .color(Colors.White)
                    .textAlign(TextAlign.Center)
                    .styleModifier { property("line-height", "1.8"); property("opacity", "0.9") },
            )

            SpanText(
                text = CONTENTS_TITLE,
                modifier = Modifier
                    .padding(top = 40.px, bottom = 8.px)
                    .fontFamily(FontFamily)
                    .fontSize(if (isMobile) 18.px else clamp(min = 18.px, value = 1.5.vw, max = 24.px))
                    .fontWeight(FontWeight.Bold)
                    .color(Colors.White),
            )

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex-wrap", "wrap")
                        property("justify-content", "center")
                    },
                horizontalArrangement = Arrangement.spacedBy(20.px),
            ) {
                CONTENT_PILLARS.forEach { (emoji, title, desc) ->
                    ContentPillarCard(
                        emoji = emoji,
                        title = title,
                        description = desc,
                        modifier = if (isMobile)
                            Modifier.fillMaxWidth().margin(bottom = 16.px)
                        else
                            Modifier
                                .styleModifier { property("width", "calc((100% - 80px) / 5)") }
                                .margin(bottom = 16.px),
                    )
                }
            }
        }
    }
}

/**
 * ContentPillarCard
 * Single card: large emoji icon, bold gradient title, short description — white bg with shadow.
 *
 * @param emoji       Icon character for this content category.
 * @param title       Category name shown in bold gradient.
 * @param description One-liner category description.
 * @param modifier    Outer modifier — caller controls width and margin.
 */
@Composable
private fun ContentPillarCard(
    emoji: String,
    title: String,
    description: String,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .backgroundColor(Colors.White)
            .borderRadius(16.px)
            .boxShadow(blurRadius = 20.px, color = Colors.LightGray)
            .padding(all = 24.px),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(10.px),
    ) {
        SpanText(
            text = emoji,
            modifier = Modifier.fontSize(36.px),
        )
        SpanText(
            text = title,
            modifier = Modifier
                .fontFamily(FontFamily)
                .fontSize(15.px)
                .fontWeight(FontWeight.Bold)
                .moxTextGradient(),
        )
        SpanText(
            text = description,
            modifier = Modifier
                .fontFamily(FontFamily)
                .fontSize(13.px)
                .fontWeight(FontWeight.Normal)
                .color(DarkGrayColor)
                .textAlign(TextAlign.Center)
                .styleModifier { property("line-height", "1.6") },
        )
    }
}


// ─────────────────────────────────────────────────────────────────────────────
// 3. Meet Mox Section — App Section bg (white)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AboutMoxSection
 * White-background section (App Section style) introducing Mox and the team.
 * Top: horizontal-scroll image carousel using [MOX_GALLERY_IMAGES] placeholders.
 * Bottom: centered bio text and team tagline.
 *
 * @param isMobile True when rendering on a small screen.
 */
@Composable
private fun AboutMoxSection(isMobile: Boolean) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(WhiteBG)
            .padding(topBottom = 80.px),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(0.px),
        ) {
            // Horizontal-scroll photo carousel
            MoxGalleryCarousel(isMobile)

            // Bio text block
            Column(
                modifier = if (isMobile)
                    Modifier.fillMaxWidth().padding(leftRight = 32.px, top = 40.px)
                else
                    Modifier.width(clamp(min = 700.px, value = 55.vw, max = 1000.px)).padding(top = 48.px),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.px),
            ) {
                TitleText(title = MOX_TITLE, color = DarkTextColor)

                SpanText(
                    text = MOX_DESC,
                    modifier = Modifier
                        .fillMaxWidth()
                        .fontFamily(FontFamily)
                        .fontSize(if (isMobile) 14.px else clamp(min = 14.px, value = 1.1.vw, max = 17.px))
                        .fontWeight(FontWeight.Normal)
                        .color(DarkGrayColor)
                        .textAlign(TextAlign.Center)
                        .styleModifier { property("line-height", "1.8") },
                )

                SpanText(
                    text = TEAM_TAGLINE,
                    modifier = Modifier
                        .padding(top = 8.px)
                        .fontFamily(FontFamily)
                        .fontSize(if (isMobile) 13.px else clamp(min = 13.px, value = 1.vw, max = 16.px))
                        .fontWeight(FontWeight.Bold)
                        .moxTextGradient()
                        .styleModifier { property("letter-spacing", "0.06em") },
                )
            }
        }
    }
}

/**
 * MoxGalleryCarousel
 * Horizontally scrollable photo strip for the Meet Mox section.
 * Each item is a fixed-size rounded image card; blank src renders a gray placeholder.
 * Images are sourced from [MOX_GALLERY_IMAGES] — replace empty strings with real paths when ready.
 *
 * @param isMobile True when rendering on a small screen — adjusts card size.
 */
@Composable
private fun MoxGalleryCarousel(isMobile: Boolean) {
    val cardSize = if (isMobile) 220.px else 280.px

    Row(
        modifier = WallpaperCarouselStyle.toModifier()
            .fillMaxWidth()
            .padding(leftRight = if (isMobile) 16.px else 40.px),
        horizontalArrangement = Arrangement.spacedBy(16.px),
        verticalAlignment = Alignment.Top,
    ) {
        MOX_GALLERY_IMAGES.forEachIndexed { index, src ->
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("width", if (isMobile) "220px" else "280px")
                        property("flex-shrink", "0")
                        property("aspect-ratio", "3 / 4")
                        property("border-radius", "16px")
                        property("overflow", "hidden")
                        property("background-color", "#e0e0e0")
                    },
                contentAlignment = Alignment.Center,
            ) {
                if (src.isNotBlank()) {
                    Image(
                        src = src,
                        description = "Mox photo ${index + 1}",
                        modifier = Modifier
                            .fillMaxWidth()
                            .fillMaxHeight()
                            .styleModifier {
                                property("object-fit", "cover")
                                property("display", "block")
                            }
                            .attrsModifier { attr("loading", "lazy") },
                    )
                } else {
                    // Gray placeholder shown until a real photo is provided
                    SpanText(
                        text = "📷",
                        modifier = Modifier
                            .fontSize(40.px)
                            .styleModifier { property("opacity", "0.4") },
                    )
                }
            }
        }
    }
}


// ─────────────────────────────────────────────────────────────────────────────
// 4. Connect Section — Dashboard About Section bg (wood)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AboutConnectSection
 * Wooden-background section (Dashboard About Section style) with social icons and email CTA.
 *
 * @param isMobile True when rendering on a small screen.
 */
@OptIn(DelicateApi::class)
@Composable
private fun AboutConnectSection(isMobile: Boolean) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("position", "relative")
                property("background-image", "url('$IMG_WOOD_TOP')")
                property("background-size", "cover")
                property("background-position", "center")
            },
        contentAlignment = Alignment.Center,
    ) {
        // Absolute-positioned background that always matches the content height
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("height", "100%")
                    property("z-index", "0")
                },
        ) {
            Image(
                src = IMG_WOOD_TOP,
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1")
                        property("object-fit", "cover")
                        property("display", "block")
                        property("min-height", "0")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
            Image(
                src = IMG_WOOD_BOTTOM,
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1")
                        property("object-fit", "cover")
                        property("display", "block")
                        property("min-height", "0")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
        }

        // Content overlay — drives the section height
        Column(
            modifier = (if (isMobile)
                Modifier.fillMaxWidth().padding(leftRight = 24.px, topBottom = 60.px)
            else
                Modifier
                    .width(clamp(min = 600.px, value = 50.vw, max = 900.px))
                    .padding(topBottom = 80.px))
                .styleModifier { property("z-index", "1") },
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(0.px),
        ) {
            TitleText(title = CONNECT_TITLE)

            DescriptionText(
                description = CONNECT_DESC,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 16.px, bottom = 32.px)
                    .textAlign(TextAlign.Center),
            )

            Row(
                modifier = Modifier.padding(bottom = 32.px),
                horizontalArrangement = Arrangement.spacedBy(24.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SocialIcon(iconUrl = SOCIAL_FACEBOOK_ICON,  link = SOCIAL_FACEBOOK_LINK,        description = "TopWithMox on Facebook")
                SocialIcon(iconUrl = SOCIAL_INSTAGRAM_ICON, link = SOCIAL_INSTAGRAM_LINK,       description = "TopWithMox on Instagram")
                SocialIcon(iconUrl = SOCIAL_YOUTUBE_ICON,   link = SOCIAL_YOUTUBE_LINK,         description = "TopWithMox on YouTube", wideIcon = true)
                SocialIcon(iconUrl = SOCIAL_TIKTOK_ICON,    link = SOCIAL_TIKTOK_LINK,          description = "TopWithMox on TikTok")
                SocialIcon(iconUrl = SOCIAL_EMAIL_ICON,     link = AboutStatics.EMAIL_LINK,     description = "Email TopWithMox")
            }

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .margin(bottom = 32.px)
                    .styleModifier {
                        property("background", "linear-gradient(to right, transparent, rgba(255,255,255,0.4), transparent)")
                    },
            )

            SpanText(
                text = EMAIL_LABEL,
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(12.px)
                    .fontWeight(FontWeight.Normal)
                    .letterSpacing(0.15.cssRem)
                    .color(Colors.White)
                    .styleModifier { property("text-transform", "uppercase"); property("opacity", "0.75") },
            )

            Box(modifier = Modifier.padding(top = 12.px, bottom = 8.px)) {
                SocialIcon(
                    iconUrl = SOCIAL_MAIL_ICON,
                    link = EMAIL_LINK,
                    description = "Email TopWithMox",
                    wideIcon = true,
                )
            }

            SpanText(
                text = EMAIL_ADDRESS,
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(if (isMobile) 14.px else clamp(min = 14.px, value = 1.1.vw, max = 17.px))
                    .fontWeight(FontWeight.Normal)
                    .color(Colors.White)
                    .styleModifier {
                        property("cursor", "pointer")
                        property("text-decoration", "underline")
                    }
                    .onClick { window.open(EMAIL_LINK, "_self") },
            )
        }
    }
}
